import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ShieldCheck, TrendingUp, TrendingDown, Minus, AlertTriangle, ChevronRight, ChevronDown, Zap } from 'lucide-react';
import { theme, cardStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  NABHComplianceWidget — collapsible. Default view keeps the title,
//  completion line, Open button and 4 mini stats visible. Score panel,
//  KPI traffic lights, trend delta and alert banner are behind a
//  chevron toggle so the dashboard stays compact.
// ═══════════════════════════════════════════════════════════════════

const badgeColors = {
  green: { bg: '#dcfce7', border: '#16a34a', txt: '#15803d', label: 'AUDIT-READY' },
  amber: { bg: '#fef3c7', border: '#ca8a04', txt: '#a16207', label: 'NEEDS WORK' },
  red:   { bg: '#fee2e2', border: '#dc2626', txt: '#b91c1c', label: 'AT RISK' },
};

const NABHComplianceWidget = ({ API_BASE, onNavigate }) => {
  const [data, setData] = useState(null);
  const [kpiSnap, setKpiSnap] = useState({ months: [], grid: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [comp, kpi] = await Promise.all([
        axios.get(`${API_BASE}/registers/compliance`),
        axios.get(`${API_BASE}/kpi/dashboard?months=2`).catch(() => ({ data: { months: [], grid: [] } })),
      ]);
      setData(comp.data);
      setKpiSnap(kpi.data || { months: [], grid: [] });
    } catch { /* silent */ } finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return null;

  const overall = data.overall || {};
  const badge = badgeColors[overall.badge] || badgeColors.red;

  // Compute KPI traffic lights for current month
  const currentMonth = kpiSnap.months[kpiSnap.months.length - 1];
  const lights = (kpiSnap.grid || []).map(row => {
    const entry = row.values?.[row.values.length - 1];
    if (!entry || row.metric.target == null) return { metric: row.metric, status: 'no-data' };
    const v = entry.value, t = row.metric.target;
    let status;
    if (row.metric.targetDirection === 'lower-is-better') {
      status = v <= t ? 'on-target' : v <= t * 1.2 ? 'at-risk' : 'breach';
    } else {
      status = v >= t ? 'on-target' : v >= t * 0.8 ? 'at-risk' : 'breach';
    }
    return { metric: row.metric, value: v, status };
  });
  const lightCounts = lights.reduce((acc, l) => { acc[l.status] = (acc[l.status] || 0) + 1; return acc; }, {});

  // Compute 7-day trend delta (last 7 days vs prior 7 days)
  let trendDelta = null;
  if (Array.isArray(data.trend) && data.trend.length >= 7) {
    const last7  = data.trend.slice(-7);
    const prior7 = data.trend.slice(-14, -7);
    const score = (arr) => arr.length ? Math.round(arr.reduce((s, t) => s + (t.score || 0), 0) / arr.length) : null;
    const s1 = score(last7), s2 = score(prior7);
    if (s1 != null && s2 != null) trendDelta = s1 - s2;
  }

  return (
    <div style={{
      ...cardStyle, padding: 12, marginBottom: 14,
      borderLeft: `4px solid ${badge.border}`,
    }}>
      {/* HEADER ROW — always visible */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 8, background: badge.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <ShieldCheck size={18} color={badge.txt}/>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14.5, color: theme.navy }}>NABH Compliance</h3>
            <span style={{
              fontSize: 11, fontWeight: 800, color: badge.txt,
              padding: '1px 8px', background: badge.bg, borderRadius: 999,
              border: `1px solid ${badge.border}33`,
            }}>
              {overall.score ?? 0}% · {badge.label}
            </span>
          </div>
          <p style={{ margin: '2px 0 0', fontSize: 11.5, color: theme.textMuted }}>
            {overall.completed || 0} of {(overall.total || 0) - (overall.skipped || 0)} register entries completed
          </p>
        </div>
        {onNavigate && (
          <button onClick={() => onNavigate('registers')} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: theme.primary, fontWeight: 700, fontSize: 12,
            display: 'inline-flex', alignItems: 'center', gap: 3,
          }}>
            Open <ChevronRight size={13}/>
          </button>
        )}
        <button
          onClick={() => setExpanded(e => !e)}
          title={expanded ? 'Collapse details' : 'Expand details'}
          style={{
            background: 'none', border: `1px solid ${theme.border}`,
            borderRadius: 6, padding: '4px 6px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', color: theme.textMuted,
          }}
        >
          <ChevronDown size={14} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}/>
        </button>
      </div>

      {/* Mini stats row — always visible */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginTop: 10 }}>
        <Stat label="Pending"  value={overall.pending  || 0} color={theme.warning} />
        <Stat label="Overdue"  value={overall.overdue  || 0} color={theme.danger} bold={overall.overdue > 0}/>
        <Stat label="Done"     value={overall.completed|| 0} color={theme.success}/>
        <Stat label="Skipped"  value={overall.skipped  || 0} color={theme.textMuted}/>
      </div>

      {/* COLLAPSIBLE: trend, KPI lights, alert */}
      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${theme.borderLight || '#eee'}` }}>
          {trendDelta != null && (
            <div style={{
              fontSize: 11.5, color: trendDelta >= 0 ? theme.success : theme.danger,
              fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8,
            }}>
              {trendDelta > 0 ? <TrendingUp size={12}/> : trendDelta < 0 ? <TrendingDown size={12}/> : <Minus size={12}/>}
              {Math.abs(trendDelta)}% vs prior week
            </div>
          )}

          {lights.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Zap size={11} color={theme.primary}/>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  KPIs ({currentMonth || 'this month'})
                </span>
                <span style={{ marginLeft: 'auto', fontSize: 10.5, color: theme.textMuted }}>
                  ✓ {lightCounts['on-target'] || 0} · ⚠ {lightCounts['at-risk'] || 0} · ✗ {lightCounts['breach'] || 0}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {lights.map((l, i) => {
                  const c = l.status === 'on-target' ? theme.success
                          : l.status === 'at-risk'   ? theme.warning
                          : l.status === 'breach'    ? theme.danger
                          : '#cbd5e1';
                  const sym = l.status === 'on-target' ? '✓'
                            : l.status === 'at-risk'   ? '⚠'
                            : l.status === 'breach'    ? '✗' : '—';
                  return (
                    <button key={i} onClick={() => onNavigate && onNavigate('kpi')}
                      title={`${l.metric.code} — ${l.metric.name}: ${l.value ?? 'no data'} ${l.metric.unit} (target ${l.metric.target ?? '—'})`}
                      style={{
                        padding: '3px 7px', borderRadius: 999, border: `1px solid ${c}55`,
                        background: c + '14', color: c, cursor: 'pointer',
                        fontSize: 10.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3,
                      }}>
                      <span style={{ fontSize: 10 }}>{sym}</span> {l.metric.code}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(overall.overdue || 0) > 0 && (
            <div style={{
              marginTop: 8, padding: '6px 10px', borderRadius: 6,
              background: theme.danger + '12', border: `1px solid ${theme.danger}44`,
              color: theme.danger, fontSize: 11.5, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <AlertTriangle size={12}/>
              {overall.overdue} overdue tasks — auditor will flag these
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color, bold }) => (
  <div style={{
    padding: '6px 8px', borderRadius: 6, background: 'white', border: `1px solid ${color}22`,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  }}>
    <div style={{ fontSize: 16, fontWeight: bold ? 800 : 700, color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 9.5, fontWeight: 700, color: theme.textMuted, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
  </div>
);

export default NABHComplianceWidget;
